/**
 * LessonPreviewModal — inline modal that renders a lesson EXACTLY as
 * a student sees it on the public course display. Loads the shared
 * LessonViewer route inside an iframe so we always match the real
 * page (rich body, quiz, images, citations, ads) without maintaining
 * a duplicate renderer.
 *
 * Wired into the AI Lesson Builder + LessonForm + LessonCard so admins
 * can preview generated content in one click without navigating away
 * from the editor.
 */
import React, { useContext } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { ExternalLink, X } from 'lucide-react';
import { AuthContext } from '../../../App';

export default function LessonPreviewModal({ open, onOpenChange, lessonId, lessonTitle }) {
  const { user } = useContext(AuthContext);
  if (!lessonId) return null;
  const src = `/lesson/${lessonId}?admin_preview=1`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[95vw] h-[90vh] p-0 gap-0 flex flex-col overflow-hidden"
        data-testid="lesson-preview-modal"
      >
        <DialogHeader className="p-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="text-base truncate" data-testid="lesson-preview-title">
                Preview — {lessonTitle || 'Lesson'}
              </DialogTitle>
              <DialogDescription className="text-xs">
                Rendered exactly as {user?.role === 'admin' ? 'a student' : 'you'} will see it on the public course page.
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => window.open(`/lesson/${lessonId}`, '_blank', 'noopener,noreferrer')}
                data-testid="lesson-preview-open-tab-btn"
                title="Open in new tab"
              >
                <ExternalLink className="w-4 h-4 mr-1.5" />
                Open in tab
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                data-testid="lesson-preview-close-btn"
                aria-label="Close preview"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>
        <div className="flex-1 bg-slate-50 dark:bg-slate-900 overflow-hidden">
          <iframe
            title={`Preview: ${lessonTitle || lessonId}`}
            src={src}
            className="w-full h-full border-0"
            data-testid="lesson-preview-iframe"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
